import React, { useEffect, useMemo, useState } from 'react';

const resolveAppBase = () => {
  if (typeof window === 'undefined') return '/';
  const explicitBase = process.env.PUBLIC_URL || '';
  let base = explicitBase;
  if (!base || base === '/') {
    const path = window.location.pathname || '/';
    base = path === '/app' || path.startsWith('/app/') ? '/app' : '/';
  }
  return base.endsWith('/') ? base.slice(0, -1) : base;
};

const resolveApiBase = () => {
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  return apiBase || '/api';
};

const resolveHref = (href, appBase) => {
  if (!href) return '#';
  if (/^(https?:)?\/\//i.test(href) || href.startsWith('#')) return href;
  if (href.startsWith('/')) return href;
  if (href === 'tv') return `${appBase}/tv`;
  return href;
};

const isPartnershipButton = (text, url) => {
  const normalizedText = String(text || '').toLowerCase();
  const normalizedUrl = String(url || '').toLowerCase();
  return (
    normalizedText.includes('partnership') ||
    normalizedUrl === '#partnership' ||
    normalizedUrl === '/partnership/apply'
  );
};

const PublicHomepage = ({ layoutKey = 'homepage', activePage = 'home' }) => {
  const [layout, setLayout] = useState(null);
  const [bioProfiles, setBioProfiles] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showApplicationsLink, setShowApplicationsLink] = useState(true);

  const appBase = useMemo(() => resolveAppBase(), []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      try {
        const endpoint = `${resolveApiBase()}/public/homepage?key=${encodeURIComponent(layoutKey)}`;
        const response = await fetch(endpoint, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data?.message || 'Unable to load homepage.');
        }
        if (!mounted) return;
        setLayout(data.layout || null);
        setBioProfiles(Array.isArray(data.bio_profiles) ? data.bio_profiles : []);
        setError('');
      } catch (requestError) {
        if (!mounted) return;
        setError(requestError.message || 'Unable to load homepage.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [layoutKey]);

  useEffect(() => {
    let mounted = true;

    const loadSiteFlags = async () => {
      try {
        const response = await fetch(`${resolveApiBase()}/public/site-flags`, {
          headers: { Accept: 'application/json' },
        });
        const data = await response.json();
        if (!response.ok || !mounted) return;
        setShowApplicationsLink(!Boolean(data?.applications_maintenance_enabled));
      } catch (requestError) {
        if (!mounted) return;
      }
    };

    loadSiteFlags();
    return () => {
      mounted = false;
    };
  }, []);

  const maxWidth = Number(layout?.settings?.max_width || 1200);
  const backgroundColor = layout?.settings?.background_color || '#f4f4ef';
  const textColor = layout?.settings?.text_color || '#1f1f1f';
  const pageTitle = layout?.settings?.page_title || 'Kingdom Vision International Network';

  const appBaseWithSlash = appBase ? `${appBase}/` : '/';
  const homeHref = appBaseWithSlash;
  const tvHref = `${appBase || ''}/tv` || '/tv';
  const radioHref = process.env.REACT_APP_PUBLIC_RADIO_URL || 'https://heavensembassy.radio12345.com';
  const aboutHref = `${appBase || ''}/about-us` || '/about-us';
  const servicesHref = `${appBase || ''}/services` || '/services';
  const applicationsHref = `${appBase || ''}/applications` || '/applications';
  const heraldHref = '/herald';
  const loginHref = `${appBase || ''}/login` || '/login';

  return (
    <main style={{ minHeight: '100vh', background: backgroundColor, color: textColor }}>
      <style>
        {`
          .pub-home-topbar { position: sticky; top: 0; z-index: 10; backdrop-filter: blur(8px); background: rgba(255,255,255,0.92); border-bottom: 1px solid rgba(0,0,0,0.08); }
          .pub-home-inner { width: min(100% - 2rem, ${maxWidth}px); margin: 0 auto; min-height: 68px; display: flex; align-items: center; justify-content: space-between; gap: 1rem; }
          .pub-home-brand { font-weight: 700; font-size: clamp(1rem, 1.5vw, 1.2rem); text-decoration: none; color: #1f1f1f; }
          .pub-home-actions { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
          .pub-home-link { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; font-weight: 600; font-size: 0.92rem; border-radius: 999px; padding: 0.62rem 0.95rem; border: 1px solid transparent; color: #201600; }
          .pub-home-link:hover { background: rgba(32,22,0,0.07); border-color: rgba(32,22,0,0.12); }
          .pub-home-link.active { background: #201600; border-color: #201600; color: #fff7e7; }
          .pub-home-login { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; font-weight: 700; font-size: 0.95rem; border-radius: 999px; padding: 0.65rem 1.05rem; border: 1px solid #201600; color: #201600; }
          .pub-home-login:hover { background: #201600; color: #fff7e7; }
          .pub-home-container { position: relative; z-index: 2; width: min(100% - 2rem, ${maxWidth}px); margin: 0 auto; }
          .pub-home-grid { display: grid; grid-template-columns: repeat(12, minmax(0, 1fr)); gap: 24px; }
          .pub-home-col { display: flex; flex-direction: column; gap: 0.9rem; min-width: 0; }
          .pub-home-col[data-align="center"] { text-align: center; align-items: center; }
          .pub-home-col[data-align="right"] { text-align: right; align-items: flex-end; }
          .pub-home-button { display: inline-block; text-decoration: none; border: 1px solid transparent; }
          .pub-home-bios { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 1rem; }
          .pub-home-bio { background: #fff; border-radius: 16px; overflow: hidden; border: 1px solid rgba(32,22,0,0.1); box-shadow: 0 10px 32px rgba(10,12,21,0.07); display: flex; flex-direction: column; }
          .pub-home-bio img { width: 100%; aspect-ratio: 1/1; object-fit: cover; background: #e8dcc4; }
          .pub-home-bio-body { padding: 1rem; display: grid; gap: 0.65rem; }
          .pub-home-bio-link { display: inline-flex; align-items: center; justify-content: center; text-decoration: none; font-weight: 700; font-size: 0.92rem; border-radius: 999px; padding: 0.55rem 0.95rem; border: 1px solid #2a1d08; color: #2a1d08; width: max-content; }
          .pub-home-section { position: relative; overflow: hidden; }
          .pub-home-bg { position: absolute; inset: 0; background-position: center; background-repeat: no-repeat; background-size: cover; z-index: 0; }
          .pub-home-overlay { position: absolute; inset: 0; z-index: 1; }
          @media (max-width: 900px) { .pub-home-grid { grid-template-columns: 1fr; } .pub-home-col { grid-column: 1 / -1 !important; } }
          @media (max-width: 700px) { .pub-home-inner { min-height: auto; padding: 0.6rem 0; flex-direction: column; align-items: flex-start; } }
        `}
      </style>

      <header className="pub-home-topbar">
        <div className="pub-home-inner">
          <a className="pub-home-brand" href={homeHref}>
            {pageTitle}
          </a>
          <div className="pub-home-actions">
            <a className={`pub-home-link ${activePage === 'home' ? 'active' : ''}`} href={homeHref}>
              Home
            </a>
            <a className={`pub-home-link ${activePage === 'tv' ? 'active' : ''}`} href={tvHref}>
              TV
            </a>
            <a className="pub-home-link" href={radioHref}>
              Radio
            </a>
            <a className={`pub-home-link ${activePage === 'about' ? 'active' : ''}`} href={aboutHref}>
              About Us
            </a>
            <a className={`pub-home-link ${activePage === 'services' ? 'active' : ''}`} href={servicesHref}>
              Services
            </a>
            {showApplicationsLink && (
              <a className={`pub-home-link ${activePage === 'applications' ? 'active' : ''}`} href={applicationsHref}>
                Applications
              </a>
            )}
            <a className={`pub-home-link ${activePage === 'herald' ? 'active' : ''}`} href={heraldHref}>
              Herald
            </a>
            <a className="pub-home-login" href={loginHref}>
              Login
            </a>
          </div>
        </div>
      </header>

      {loading && (
        <div style={{ width: 'min(100% - 2rem, 1200px)', margin: '2rem auto' }}>
          Loading homepage...
        </div>
      )}
      {error && (
        <div style={{ width: 'min(100% - 2rem, 1200px)', margin: '2rem auto', color: '#9f1239' }}>
          {error}
        </div>
      )}

      {!loading &&
        !error &&
        Array.isArray(layout?.sections) &&
        layout.sections.map((section) => {
          const paddingTop = Number(section?.padding?.top ?? 70);
          const paddingBottom = Number(section?.padding?.bottom ?? 70);
          const gap = Number(section?.gap ?? 24);
          const bgType = section?.background?.type || 'color';
          const bgValue = section?.background?.value || '#ffffff';
          const overlay = section?.background?.overlay || '';
          const columns = Array.isArray(section?.columns) ? section.columns : [];
          const isBios = section?.source === 'bios' || section?.id === 'leadership';

          return (
            <section
              key={section?.id || Math.random()}
              id={section?.id || 'section'}
              className="pub-home-section"
              style={{
                paddingTop: `${paddingTop}px`,
                paddingBottom: `${paddingBottom}px`,
                background: bgType === 'color' ? bgValue : 'transparent',
              }}
            >
              {bgType === 'image' && bgValue && (
                <div className="pub-home-bg" style={{ backgroundImage: `url('${bgValue}')` }} />
              )}
              {overlay ? <div className="pub-home-overlay" style={{ background: overlay }} /> : null}

              <div className="pub-home-container">
                {isBios ? (
                  <div style={{ display: 'grid', gap: '1rem' }}>
                    <h2 style={{ margin: 0, fontSize: 'clamp(1.7rem, 2.6vw, 2.3rem)', color: '#2a1d08' }}>
                      {section?.name || 'Leadership Bios'}
                    </h2>
                    <div className="pub-home-bios">
                      {bioProfiles.length ? (
                        bioProfiles.map((profile) => (
                          <article key={profile?.slug || profile?.id} className="pub-home-bio">
                            <img src={profile?.thumbnail_url || ''} alt={profile?.name || 'Leader'} loading="lazy" />
                            <div className="pub-home-bio-body">
                              <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#2a1d08' }}>{profile?.name || ''}</h3>
                              <p style={{ margin: 0, color: '#6b5124', lineHeight: 1.5, fontSize: '0.92rem' }}>
                                {profile?.title || ''}
                              </p>
                              <a className="pub-home-bio-link" href={`/bios/${profile?.slug || ''}`}>
                                Read Bio
                              </a>
                            </div>
                          </article>
                        ))
                      ) : (
                        <p style={{ margin: 0 }}>No bios available yet.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="pub-home-grid" style={{ gap: `${gap}px` }}>
                    {columns.map((column, colIndex) => {
                      const width = Math.max(1, Math.min(12, Number(column?.width || 12)));
                      const align = column?.align || 'left';
                      const items = Array.isArray(column?.items) ? column.items : [];

                      return (
                        <div
                          key={column?.id || colIndex}
                          className="pub-home-col"
                          data-align={align}
                          style={{ gridColumn: `span ${width}` }}
                        >
                          {items.map((item, itemIndex) => {
                            const itemType = item?.type || 'text';
                            if (itemType === 'text') {
                              const styles = item?.styles || {};
                              const lines = String(item?.content || '').split('\n');
                              return (
                                <p
                                  key={item?.id || itemIndex}
                                  style={{
                                    margin: 0,
                                    color: styles?.color || '#111111',
                                    fontSize: `${Number(styles?.font_size || 18)}px`,
                                    fontWeight: Number(styles?.font_weight || 400),
                                    fontStyle: styles?.font_style || 'normal',
                                    lineHeight: Number(styles?.line_height || 1.5),
                                  }}
                                >
                                  {lines.map((line, lineIndex) => (
                                    <React.Fragment key={lineIndex}>
                                      {line}
                                      {lineIndex < lines.length - 1 ? <br /> : null}
                                    </React.Fragment>
                                  ))}
                                </p>
                              );
                            }

                            if (itemType === 'image') {
                              const styles = item?.styles || {};
                              const src = item?.src || '';
                              if (!src) return null;
                              return (
                                <img
                                  key={item?.id || itemIndex}
                                  src={src}
                                  alt={item?.alt || 'Image'}
                                  loading="lazy"
                                  style={{
                                    width: `${Number(styles?.width || 100)}%`,
                                    objectFit: styles?.fit || 'cover',
                                    borderRadius: `${Number(styles?.border_radius || 0)}px`,
                                  }}
                                />
                              );
                            }

                            if (itemType === 'button') {
                              const styles = item?.styles || {};
                              const text = item?.text || 'Button';
                              const rawHref = String(item?.url || '#').trim();
                              const href = isPartnershipButton(text, rawHref)
                                ? '/partnership/apply'
                                : resolveHref(rawHref, appBase);
                              const isOutline = String(styles?.variant || 'solid') === 'outline';
                              const bg = styles?.background_color || '#1e3a8a';
                              return (
                                <a
                                  key={item?.id || itemIndex}
                                  className="pub-home-button"
                                  href={href}
                                  style={{
                                    background: isOutline ? 'transparent' : bg,
                                    color: styles?.text_color || '#ffffff',
                                    borderColor: bg,
                                    fontSize: `${Number(styles?.font_size || 16)}px`,
                                    fontWeight: Number(styles?.font_weight || 600),
                                    borderRadius: `${Number(styles?.border_radius || 10)}px`,
                                    padding: `${Number(styles?.padding_y || 12)}px ${Number(styles?.padding_x || 20)}px`,
                                  }}
                                >
                                  {text}
                                </a>
                              );
                            }

                            return null;
                          })}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </section>
          );
        })}
    </main>
  );
};

export default PublicHomepage;
